import { showNotification } from '@mantine/notifications'
import { MedplumClient } from '@medplum/core'
import { Encounter } from '@medplum/fhirtypes'
import type { ApiClient } from '@behuman.corp/contracts'

export async function getPatientUpcomingAppointments(
  medplum: MedplumClient,
  patientId: string,
): Promise<Encounter[] | []> {
  try {
    const bot = await medplum.searchOne('Bot', 'name=get-patient-appointments')
    if (!bot) {
      throw new Error('Missing bot')
    }
    const result = await medplum.executeBot(bot.id, { patientId }, 'application/fhir+json')
    if (!result.appointments) {
      throw new Error('Failed to fetch appointments')
    }
    return result.appointments
  } catch (e) {
    showNotification({
      color: 'red',
      title: 'Error',
      message: `Failed to fetch upcoming appointments, ${e}`,
    })
    return []
  }
}
export async function cancelAppointment(
  middleware: ApiClient,
  encounterId: string,
): Promise<boolean | void> {
  try {
    // **Always asks for the Zocdoc half**, unlike the Schedule dialog's checkbox. This card knows
    // only an encounter id — nothing here says whether the visit came from Zocdoc — and looking it
    // up per row to offer a choice would cost a read for every appointment on screen. Asking
    // unconditionally is safe: the middleware skips silently when the visit is not bound, and
    // refuses when the caller's role does not carry it. Cancelling here used to leave a live
    // marketplace booking behind, with nothing said.
    //
    // The third argument is what makes this a QUERY parameter; on a non-GET the fetcher would
    // otherwise put it in the body, where `@Query` never sees it.
    //
    // The cast is the contract lagging, not a shape being forced: `cancelZocdoc` is on the route
    // in `openapi.json` but the published `@behuman.corp/contracts` has not been regenerated, so
    // its `paths` type does not carry it yet. Written out rather than smuggled in behind a spread
    // — a spread silently skips excess-property checking, which is how this same parameter was
    // once shipped into the request body instead of the query.
    const result = (await middleware.delete(
      '/appointment/{encounterId}',
      { encounterId, cancelZocdoc: 'true' } as unknown as { encounterId: string },
      { cancelZocdoc: true },
    )) as {
      success: boolean
      message?: string
      zocdocCancelled?: boolean
      zocdocMessage?: string
    }
    if (!result.success) {
      throw new Error(result.message || 'Failed to cancel appointment')
    }
    // Ours is cancelled either way. A Zocdoc half that did not go through is said out loud rather
    // than folded into silence: the booking is still live there, and only a person can finish it.
    if (result.zocdocCancelled === false) {
      showNotification({
        color: 'yellow',
        title: 'Cancelled here, but not on Zocdoc',
        message: result.zocdocMessage ?? 'The Zocdoc booking is still live — cancel it there.',
        autoClose: false,
      })
    }
    return true
  } catch (e) {
    showNotification({
      color: 'red',
      title: 'Error',
      message: `Failed to cancel appointment, ${e}`,
    })
  }
}
